/**
 * Live refresh of the links screens (1.J deliverable 2): one `appEvents` subscription per mounted
 * panel. The mapping from an event to the query keys it invalidates is pure.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { appEvents } from '../editor-services/events.js';
import { useDocMeta } from './queries.js';

/** The last `metaVersion` seen per page, so typing in a body (same version) costs nothing. */
export class MetaSeen {
  private readonly versions = new Map<string, number>();

  /** Records a version read from an answer, never lowering one already seen. */
  seed(id: string, version: number): void {
    const last = this.versions.get(id);
    if (last === undefined || version > last) this.versions.set(id, version);
  }

  /** True, and recorded, when `version` is newer than the last seen (or none was seen). */
  exceeds(id: string, version: number): boolean {
    const last = this.versions.get(id);
    if (last !== undefined && version <= last) return false;
    this.versions.set(id, version);
    return true;
  }
}

export type LiveEvent =
  | { readonly type: 'links.changed'; readonly docIds: readonly string[] }
  | { readonly type: 'doc.lifecycle'; readonly docId: string }
  | { readonly type: 'doc.changed'; readonly docId: string; readonly metaVersion: number }
  | { readonly type: 'chrono.changed'; readonly docIds: readonly string[] }
  | { readonly type: 'ledger.changed'; readonly docIds: readonly string[] }
  | { readonly type: 'clock.changed' };

export interface LiveOptions {
  /** A shown link has a validity window: a moved date or calendar re-derives its spans. */
  readonly validity?: boolean;
}

/** 1.O-1's `whenKeys.placements`, spelled out so this pure module imports none of its React. */
const placementsKey = (id: string) => ['when', 'placements', id] as const;

/** The query keys an event invalidates for the page `pageId` showing the rows `shown`. */
export function keysFor(
  event: LiveEvent,
  pageId: string,
  shown: ReadonlySet<string>,
  seen: MetaSeen,
  options: LiveOptions = {},
): QueryKey[] {
  const relevant = (id: string) => id === pageId || shown.has(id);
  const metaKeys = (ids: readonly string[]) =>
    ids.filter(relevant).map((id) => ['documents', 'meta', id] as const);
  switch (event.type) {
    case 'links.changed': {
      if (!event.docIds.some(relevant)) return [];
      return [
        ['links', 'list', pageId],
        ['links', 'backlinks', pageId],
        ['links', 'unlinked', pageId],
        ['tree'],
        ...metaKeys(event.docIds),
      ];
    }
    case 'doc.lifecycle': {
      if (!relevant(event.docId)) return [];
      return [
        ['links', 'list', pageId],
        ['links', 'backlinks', pageId],
        ['links', 'unlinked', pageId],
        ['links', 'titles'],
        ['tree'],
        ...metaKeys([event.docId]),
      ];
    }
    case 'doc.changed': {
      if (!relevant(event.docId) || !seen.exceeds(event.docId, event.metaVersion)) return [];
      return [['links', 'titles'], ['tree'], ...metaKeys([event.docId])];
    }
    case 'chrono.changed': {
      const all = event.docIds.length === 0;
      const touched = all ? [pageId, ...shown] : event.docIds.filter(relevant);
      return [
        ...touched.map(placementsKey),
        ...(options.validity
          ? [['links', 'list', pageId] as const, ['links', 'backlinks', pageId] as const]
          : []),
      ];
    }
    case 'ledger.changed':
      return metaKeys(event.docIds);
    case 'clock.changed':
      // `useWhenEvents` refetches NOW; the states re-render from it.
      return [];
  }
}

function invalidateAll(client: QueryClient, keys: readonly QueryKey[]): void {
  for (const queryKey of keys) void client.invalidateQueries({ queryKey });
}

/** Subscribes the mounted panel to the five events (`clock.changed` is `useWhenEvents`'s); `shown` is read at event time. */
export function useLinksLive(
  pageId: string,
  shown: readonly string[],
  seen: MetaSeen,
  options: LiveOptions = {},
): void {
  const client = useQueryClient();
  const shownRef = useRef<ReadonlySet<string>>(new Set());
  shownRef.current = new Set(shown);
  const validityRef = useRef(false);
  validityRef.current = options.validity === true;
  useEffect(() => {
    const run = (event: LiveEvent) =>
      invalidateAll(
        client,
        keysFor(event, pageId, shownRef.current, seen, { validity: validityRef.current }),
      );
    const offs = [
      appEvents.subscribe('links.changed', (e) =>
        run({ type: 'links.changed', docIds: e.data.docIds }),
      ),
      appEvents.subscribe('doc.lifecycle', (e) =>
        run({ type: 'doc.lifecycle', docId: e.data.docId }),
      ),
      appEvents.subscribe('doc.changed', (e) =>
        run({ type: 'doc.changed', docId: e.data.docId, metaVersion: e.data.metaVersion }),
      ),
      appEvents.subscribe('chrono.changed', (e) =>
        run({ type: 'chrono.changed', docIds: e.data.docIds }),
      ),
      appEvents.subscribe('ledger.changed', (e) =>
        run({ type: 'ledger.changed', docIds: e.data.docIds }),
      ),
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [client, pageId, seen]);
}

/**
 * The `MetaSeen` of a mounted tab, seeded from the page's own `docs.meta` (the key the frame
 * reads too), so the first `doc.changed` of a body edit, which carries the version already read,
 * costs nothing.
 */
export function usePageSeen(pageId: string): MetaSeen {
  const [seen] = useState(() => new MetaSeen());
  const meta = useDocMeta(pageId);
  const version = meta.data?.metaVersion;
  useEffect(() => {
    if (version !== undefined) seen.seed(pageId, version);
  }, [seen, pageId, version]);
  return seen;
}
